import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { CorrectDetailsScreen } from './CorrectDetailsScreen'
import { UpdateAssessmentScreen } from './UpdateAssessmentScreen'
import { SyncBar } from '../components/SyncBar'
import { db, type LocalHousehold } from '../lib/db'
import { t } from '../lib/i18n'

/**
 * ONE HOUSEHOLD, AND THE TWO DIFFERENT THINGS "EDIT" CAN MEAN
 * -----------------------------------------------------------
 * Asked for by 99: "in case there is a mistake or need changes".
 *
 *   A MISTAKE — the name was typed wrong, the pin is on the wrong house.
 *               The fact was never true. Correct it; the old value lives on
 *               in the audit log, which this screen shows.
 *
 *   A CHANGE  — the family built a latrine, a child was born.
 *               The old fact WAS true on the day it was recorded. Record a
 *               new assessment; both values survive, and "what was latrine
 *               coverage last December" stays answerable.
 *
 * The screen therefore offers two buttons, not one, and says plainly which is
 * which. Collapsing them into a single "Edit" is the fastest way to destroy a
 * baseline, and it cannot be undone afterwards.
 */

type Household = LocalHousehold

type Change = {
  changed_at: string
  changed_by_name: string | null
  field: string
  old_value: string | null
  new_value: string | null
}

/** Field names as an officer would say them, not as the database spells them. */
const FIELD_LABELS: Record<string, string> = {
  head_name: 'Head of household name',
  head_sex: 'Sex of head',
  record_status: 'Record status',
  status_note: 'Status note',
  geom: 'Location',
  gps_accuracy_m: 'GPS accuracy',
  gps_provider: 'GPS source',
  gps_captured_at: 'Location captured',
  adults_male_18_59: 'Adult men 18–59',
  adults_female_18_59: 'Adult women 18–59',
  children_under_18: 'Children under 18',
  elderly_60_plus: 'People 60 and over',
  members_with_disability: 'People with a disability',
  has_latrine: 'Has a latrine',
  latrine_type: 'Latrine type',
  water_source: 'Drinking water source',
  last_assessed_at: 'Last assessed',
  household_seq: 'Household number',
}

function label(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/_/g, ' ')
}

/**
 * geom arrives as a hex blob nobody can read. Saying "moved" is honest and
 * useful; printing 0101000020E6100000… is neither.
 */
function shown(field: string, value: string | null): string {
  if (value === null || value === '') return '(empty)'
  if (field === 'geom') return 'a point on the map'
  if (value.length > 40) return value.slice(0, 40) + '…'
  return value
}

function when(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function HouseholdScreen({
  householdUid,
  villageName,
  onBack,
}: {
  householdUid: string
  villageName: string
  onBack: (changed: boolean) => void
}) {
  const [h, setH] = useState<Household | null>(null)
  const [history, setHistory] = useState<Change[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'view' | 'correct' | 'assess'>('view')
  const [saved, setSaved] = useState<string | null>(null)
  // Whether anything changed while this screen was open, so the village list
  // knows to reload rather than showing a stale row.
  const [dirty, setDirty] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)

    // From the phone. This screen opens in a village with no signal, showing
    // a household registered ten minutes ago that no server has yet seen.
    setH((await db.households.get(householdUid)) ?? null)

    // The change history lives only on the server — it is built from the audit
    // log, which is deliberately not copied onto phones. Not having it is a
    // missing convenience, never a broken screen.
    try {
      const { data: hist } = await supabase.rpc('household_change_history', {
        p_household_uid: householdUid,
      })
      setHistory((hist ?? []) as Change[])
    } catch {
      setHistory([])
    }

    setLoading(false)
  }, [householdUid])

  useEffect(() => {
    load()
  }, [load])

  if (mode === 'correct' && h) {
    return (
      <CorrectDetailsScreen
        household={h}
        onCancel={() => setMode('view')}
        onDone={(what) => {
          setMode('view')
          setSaved(what)
          setDirty(true)
          load()
        }}
      />
    )
  }

  if (mode === 'assess' && h) {
    return (
      <UpdateAssessmentScreen
        household={h}
        onCancel={() => setMode('view')}
        onDone={() => {
          setMode('view')
          setSaved('New assessment recorded. The earlier values are kept.')
          setDirty(true)
          load()
        }}
      />
    )
  }

  return (
    <div className="screen">
      <div className="appbar">
        <h1>{h?.household_code ?? 'Household'}</h1>
        <button className="btn-quiet" onClick={() => onBack(dirty)}>
          {t('common.back')}
        </button>
      </div>

      <SyncBar />

      <div className="content">
        {loading && <p className="spinner">{t('common.loading')}</p>}

        {error && <div className="alert alert-error">{error}</div>}

        {!loading && !h && !error && (
          <div className="alert alert-warn">
            This household is not on this phone. It may belong to a village you
            are not assigned to, or the phone has not refreshed since it was
            registered on another device.
          </div>
        )}

        {saved && (
          <div
            className="alert"
            style={{
              borderColor: 'var(--green-700)',
              color: 'var(--green-900)',
              background: 'var(--green-100)',
            }}
          >
            {saved}
          </div>
        )}

        {h && (
          <>
            <div className="card" style={{ maxWidth: 'none' }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {h.head_name ?? t('village.noName')}
              </div>
              <div className="muted">{villageName}</div>

              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span className="pill">{h.household_size} {t('common.people')}</span>
                {h.has_latrine === true && <span className="pill">{t('village.hasLatrine')}</span>}
                {h.has_latrine === false && <span className="pill">{t('village.noLatrine')}</span>}
                {h.record_status !== 'active' && <span className="pill">{h.record_status}</span>}
                {h.gps_accuracy_m != null && (
                  <span className="pill">±{Math.round(h.gps_accuracy_m)} m</span>
                )}
              </div>

              <table style={{ width: '100%', marginTop: 14, borderCollapse: 'collapse' }}>
                <tbody>
                  {[
                    ['Adult men 18–59', h.adults_male_18_59],
                    ['Adult women 18–59', h.adults_female_18_59],
                    ['Children under 18', h.children_under_18],
                    ['People 60 and over', h.elderly_60_plus],
                    ['People with a disability', h.members_with_disability],
                    ['Drinking water', h.water_source ?? '—'],
                    ['Latrine type', h.latrine_type ?? '—'],
                    [t('hh.lastAssessed'), h.last_assessed_at ? when(h.last_assessed_at) : t('hh.never')],
                  ].map(([k, v]) => (
                    <tr key={String(k)}>
                      <td style={{ padding: '6px 0', color: 'var(--ink-soft)' }}>{k}</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>
                        {String(v)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ---- the two kinds of edit, named for what they actually do ---- */}
            <div className="card" style={{ maxWidth: 'none', marginTop: 14 }}>
              <h3 style={{ marginTop: 0 }}>{t('hh.somethingChanged')}</h3>

              <button className="btn-primary" onClick={() => setMode('correct')}>
                {t('hh.correct')}
              </button>
              <p className="muted" style={{ marginTop: 6 }}>
                {t('hh.correctHelp')}
              </p>

              <button
                className="btn-primary"
                style={{ marginTop: 14 }}
                onClick={() => setMode('assess')}
              >
                {t('hh.assess')}
              </button>
              <p className="muted" style={{ marginTop: 6, marginBottom: 0 }}>
                {t('hh.assessHelp')}
              </p>
            </div>

            {/* ---- the audit trail, in plain words ---- */}
            <div className="card" style={{ maxWidth: 'none', marginTop: 14, marginBottom: 40 }}>
              <button
                className="btn-quiet"
                style={{ padding: '10px 0', minHeight: 44 }}
                onClick={() => setShowHistory((s) => !s)}
              >
                {showHistory ? t('hh.hide') : t('hh.show')} {t('hh.history')} ({history.length})
              </button>

              {showHistory && !navigator.onLine && (
                <p className="muted" style={{ marginBottom: 0 }}>
                  {t('hh.historyOffline')}
                </p>
              )}

              {showHistory && history.length === 0 && (
                <p className="muted" style={{ marginBottom: 0 }}>
                  {t('hh.noHistory')}
                </p>
              )}

              {showHistory && history.length > 0 && (
                <ul className="list" style={{ marginTop: 12 }}>
                  {history.map((c, i) => (
                    <li key={i}>
                      <div style={{ fontWeight: 600 }}>{label(c.field)}</div>
                      <div style={{ marginTop: 4 }}>
                        <span style={{ textDecoration: 'line-through', color: 'var(--ink-soft)' }}>
                          {shown(c.field, c.old_value)}
                        </span>{' '}
                        → <strong>{shown(c.field, c.new_value)}</strong>
                      </div>
                      <div className="muted" style={{ marginTop: 4 }}>
                        {when(c.changed_at)} · {c.changed_by_name ?? 'unknown user'}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export type { Household }
